<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          <div class="header-title">
            Submission Specifications
          </div>
        </template>
      </EsHeader>
    </div>
    <div class="content">
      <div
        style="padding-top: 1.15rem"
        v-for="eachPersonType in sortedAvailablePersonTypes"
        :key="eachPersonType.id"
      >
        <tr>
          <th>
            {{ eachPersonType.name }} Person Type
          </th>
        </tr>
        <div class="person-type">
          <div>
            <span class="rule">
              Concurrent Location Assignment:
            </span>
            <b class="left-right-padding">
              {{ eachPersonType.concurrentLocAssign === true ? 'Yes' : 'No' }}
            </b>
          </div>
          <div>
            <span class="rule left-right-padding pipeline-border">
              Status:
            </span>
            <b>
              {{ eachPersonType.status === true ? 'Active' : 'Inactive' }}
            </b>
          </div>
          <div>
            <span class="rule left-right-padding pipeline-border">
              Default:
            </span>
            <b>
              {{ eachPersonType.defaultType === true ? 'Yes' : 'No' }}
            </b>
          </div>
          <div class="icon-right">
            <a role="button" tabindex="0"
              @keyup.enter="viewPersonTypeDetails(eachPersonType)"
              title="View Details"
              @click="viewPersonTypeDetails(eachPersonType)"
            >
              <font-awesome-icon :icon="['far', 'angle-double-right']" size="2x"/>
            </a>
          </div>
        </div>
        <es-loader v-if="loading" style="margin: 5rem"/>
        <es-exception
          v-else-if="loadingError"
          exceptionMessage="There was an issue. Please try again or contact your administrator."
          margin="0"
          style="padding: 5rem"
        />
        <table v-else class="left-right-padding">
          <thead>
            <tr>
              <th
                class="no-border"
                style="font-size: .825rem; padding: 1rem 2rem 0 2.5rem"
              >
                Specifications
              </th>
            </tr>
            <tr>
              <th class="rule left-padding" title="Specifications Name">
                Name
              </th>
              <th class="rule" title="Submission Purpose">
                Submission Purpose
              </th>
              <th class="rule" title="Status">
                Status
              </th>
              <th class="rule last-child" title="Actions">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="eachSpec in sortedAllSubSpecsArray
                .filter(property => property.personType.id === eachPersonType.id)"
              :key="eachSpec.id"
              :class="{ 'disabled': !eachPersonType.status }"
            >
              <td class="left-padding">
                {{ eachSpec.name }}
              </td>
              <td>
                {{
                  eachSpec.purpose === 'ASSIGN' ? 'Assignment'
                    : eachSpec.purpose === 'ENROLL' ? 'Enrollment'
                    : eachSpec.purpose === 'SEARCH' ? 'Search'
                    : eachSpec.purpose === 'DMATCH' ? 'DirectMatch'
                    : false
                }}
              </td>
              <td>
                {{ eachSpec.enabled === true ? 'Enabled' : 'Disabled' }}
              </td>
              <td class="last-child">
                <a role="button" tabindex="0"
                  @keyup.enter="viewSpecificationDetails(eachSpec)"
                  title="View Details"
                  @click="viewSpecificationDetails(eachSpec)"
                  :class="{ 'disabled': !eachPersonType.status }"
                >
                  <font-awesome-icon
                    size="2x"
                    :icon="['far', 'angle-double-right']"
                  />
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
} from '@fortawesome/pro-regular-svg-icons';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleRight,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  created() {
    this.onLoad();
  },
  computed: {
    allSubSpecsArray() {
      return [].concat(...this.allSubmissionSpecs);
    },
    sortedAllSubSpecsArray() {
      return this.allSubSpecsArray.slice().sort(this.compare);
    },
    sortedAvailablePersonTypes() {
      return this.availablePersonTypes.slice().sort(this.compare);
    },
  },
  components: {
    EsException,
    EsHeader,
    EsLoader,
  },
  data() {
    return this.initialData();
  },
  methods: {
    compare(a, b) {
      if (a.name.toUpperCase() < b.name.toUpperCase()) {
        return -1;
      }
      if (a.name.toUpperCase() > b.name.toUpperCase()) {
        return 1;
      }
      return 0;
    },
    initialData() {
      return {
        allSubmissionSpecs: [],
        availablePersonTypes: [],
        personTypeIds: [],
        loading: false,
        loadingError: false,
      };
    },
    async loadSubmissionSpecs() {
      this.personTypeIds.forEach(async (id) => {
        try {
          this.loading = true;
          this.loadingError = false;
          const res = await axios.get(`person-types/${id}/submission-specs/`);
          this.allSubmissionSpecs.push(res.data);
        } catch (err) {
          this.loadingError = true;
        } finally {
          this.loading = false;
        }
      });
    },
    async onLoad() {
      this.availablePersonTypes = this.$store.state.user.userPersonTypes
        .map(item => item.personType);
      this.personTypeIds = this.availablePersonTypes.map(item => item.id);
      this.loadSubmissionSpecs();
    },
    viewPersonTypeDetails(sendPersonTypeData) {
      this.$emit('showPersonTypeDetails', sendPersonTypeData);
      this.$emit('attachAllPersonTypes', this.availablePersonTypes);
    },
    viewSpecificationDetails(sendSpecificationType) {
      this.$emit('showSpecificationsDetails', sendSpecificationType);
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    vertical-align: middle
  table td
    vertical-align: middle
  table tr.disabled
    color: #757575
    cursor: not-allowed
  b
    font-size: 12px
    padding-right: 0.75rem
  a.disabled
    color: #757575
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  .header
    display: flex
    align-items: center
  .icon-right
    margin-left: auto
    font-size: 12px
  .last-child
    font-size: 12px
    text-align: right
    width: 1%
    white-space: nowrap
    padding-right: 4rem
  .left-padding
    padding-left: 2.5rem
  .left-right-padding
    padding: 0 0.75rem
  .no-border
    border: 0
  .pipeline-border
    border-left: 1px solid #DEE2E6
  .person-type
    display: flex
    border-bottom: 1px solid #EEE
    padding: 1rem 0
  .rule
    font-size: 12px
    color: #666
  .top-padding
    padding-top: 2rem
  .content
    overflow-x: auto
</style>
